import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import SortableTable from "../../Pages/Components/SortableTable";
import SearchBar from "../../Pages/Components/SearchBar";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaEye, FaArrowLeft } from "react-icons/fa";
import { getSalesmanRedemptionStats } from "../../redux/actions/orderActions";

const RedeemedStatistics = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month").toDate(),
    new Date(),
  ]);
  const [startDate, endDate] = dateRange;

  const {
    loading = false,
    data: salesmanData = [],
    overall_summary: overallSummary = {},
    error = null,
  } = useSelector((state) => state.redemptionStats || {});

  useEffect(() => {
    if (!userInfo?.access_token) {
      navigate("/login");
      return;
    }

    const payload = {
      salesman_id: null,
      start_date: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      end_date: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
    };

    dispatch(getSalesmanRedemptionStats(payload, userInfo.access_token));
  }, [dispatch, userInfo, navigate, startDate, endDate]);

  const handleViewSalesman = (row) => {
    navigate(`/dashboard/redeemedstatistcis/${row.salesman.user_id}`, {
      state: { dateRange: [startDate, endDate] },
    });
  };

  // Cards navigate to a dedicated detail page instead of opening a modal
  const handleCardClick = (cardType) => {
    navigate(`/dashboard/redeemedstatistcis/summary/${cardType}`, {
      state: { dateRange: [startDate, endDate] },
    });
  };

  // Filter salesman list by search (name / mobile)
  const filteredSalesmen = salesmanData.filter((row) => {
    const search = searchValue.trim().toLowerCase();
    if (!search) return true;
    return (
      row.salesman?.name?.toLowerCase().includes(search) ||
      row.salesman?.mobile?.toString().includes(search)
    );
  });

  const columns = [
    { label: "Salesman", key: "name" },
    { label: "Mobile", key: "mobile" },
    { label: "Accumulated", key: "total_accumulated" },
    { label: "Redeemed", key: "total_redeemed" },
    { label: "Total Scanned", key: "total_scanned" },
    { label: "Actions", key: "actions" },
  ];

  const renderRow = (row) => (
    <tr
      key={row.salesman.user_id}
      className="hover:bg-amber-50 hover:text-gray-950 hover:font-semibold"
    >
      <td className="px-2 py-4 border-b-gray-400">{row.salesman.name}</td>
      <td className="px-2 py-4 border-b-gray-400">{row.salesman.mobile}</td>
      <td className="px-2 py-4 border-b-gray-400">
        {row.summary.total_accumulated}
      </td>
      <td className="px-2 py-4 border-b-gray-400 font-semibold text-pink-600">
        {row.summary.total_redeemed}
      </td>
      <td className="px-2 py-4 border-b-gray-400">
        {row.summary.total_scanned}
      </td>
      <td className="px-2 py-4 border-b-gray-400">
        <button
          onClick={() => handleViewSalesman(row)}
          className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium"
        >
          <FaEye /> View
        </button>
      </td>
    </tr>
  );

  return (
    <DashboardLayout>
      <div className="bg-priimary max-w-full mx-auto px-2">
        <div className="flex items-center space-x-3 py-4">
          <button
            onClick={() => navigate(-1)}
            className="hover:text-gray-800"
          >
            <FaArrowLeft size={20} />
          </button>
          <h2 className="text-2xl font-bold text-amber-600 mb-1">
            Salesman Redemption Statistics
          </h2>
        </div>

        {/* Filters */}
        <div className="w-2/3 flex flex-col sm:flex-row items-center justify-start gap-4 mb-4">
          <SearchBar
            value={searchValue}
            onChange={setSearchValue}
            placeholder="Search by Salesman / Mobile"
          />

          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            isClearable
            placeholderText="Select date range"
            dateFormat="dd MMM yyyy"
            className="w-1.4 px-4 py-2 border border-gray-950 rounded-md text-sm placeholder-gray-950"
          />
        </div>

        {/* Overall Summary Cards — each navigates to its own detail page */}
        {!loading && !error && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div
              onClick={() => handleCardClick("salesmen")}
              className="bg-white p-4 rounded-xl shadow-md border border-amber-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
            >
              <p className="text-xs text-gray-500">Total Salesmen</p>
              <p className="text-xl font-bold text-gray-950">
                {overallSummary.total_salesmen ?? 0}
              </p>
            </div>
            <div
              onClick={() => handleCardClick("accumulated")}
              className="bg-white p-4 rounded-xl shadow-md border border-amber-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
            >
              <p className="text-xs text-gray-500">Total Accumulated</p>
              <p className="text-xl font-bold text-gray-950">
                {overallSummary.overall_accumulated ?? 0}
              </p>
            </div>
            <div
              onClick={() => handleCardClick("redeemed")}
              className="bg-white p-4 rounded-xl shadow-md border border-amber-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
            >
              <p className="text-xs text-gray-500">Total Redeemed</p>
              <p className="text-xl font-bold text-gray-950">
                {overallSummary.overall_redeemed ?? 0}
              </p>
            </div>
            <div
              onClick={() => handleCardClick("scanned")}
              className="bg-white p-4 rounded-xl shadow-md border border-amber-500 cursor-pointer hover:shadow-lg transition-shadow duration-200"
            >
              <p className="text-xs text-gray-500">Total Scanned</p>
              <p className="text-xl font-bold text-gray-950">
                {overallSummary.overall_scanned ?? 0}
              </p>
            </div>
          </div>
        )}

        {/* Salesman Table */}
        {loading ? (
          <p className="text-center text-gray-500">Loading ...</p>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : filteredSalesmen.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            No redemption data found for the selected filters.
          </p>
        ) : (
          <div className="overflow-x-auto rounded">
            <SortableTable
              columns={columns}
              data={filteredSalesmen}
              renderRow={renderRow}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default RedeemedStatistics;